if (!customElements.get('clerk-recommendations')) {
  customElements.define(
    'clerk-recommendations',
    class ClerkRecommendations extends HTMLElement {
      constructor() {
        super();

        /** @type {import('./types').ItemCarousel | null} */
        this.carousel = this.querySelector('item-carousel');
        this.list = this.querySelector('[data-products-list]');
        this.endpoint = this.dataset.endpoint || 'recommendations/popular';
        this.limit = this.dataset.limit || 12;
        this.productId = this.dataset.productId || null;
        this.controller = null;
      }

      async connectedCallback() {
        this.setAttribute('data-loading', 'true');

        const products = await this.getRecommendedProducts();
        const cards = await this.getProductCards(products);

        if (this.carousel && this.list && cards.length > 0) {
          this.list.innerHTML = '';

          cards.forEach((card) => {
            this.list?.append(card);
          });

          this.carousel?.instance?.update();
          this.enableClerkClickTracking();

          this.removeAttribute('data-loading');
        }
      }

      enableClerkClickTracking() {
        if (typeof window.Clerk === 'function') {
          window.Clerk('click', '*[data-clerk-product-id]');
        }
      }

      /**
       * @returns {Promise<import('./types').ClerkProductData[]>} - The recommended Clerk products
       */
      async getRecommendedProducts() {
        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        const response = await fetch(
          `https://api.clerk.io/v2/${this.endpoint}?key=nqfwzYFvqB1afrgxh6Dm62gL7yFOnyod&limit=${this.limit}&visitor=auto&attributes=url,id,tags&${this.productId ? `&products=[${this.productId}]` : ''}&filter=tags+contains%20not+"combined-listing"`,
          { method: 'GET', headers: { accept: 'application/json' }, signal },
        ).catch((error) => {
          if (error.name === 'AbortError') {
            return null;
          }

          throw error;
        });

        if (response && response.ok) {
          const data = await response.json();

          return (data.product_data || []).map((/** @type {import('./types').ClerkProductData} */ product) => ({
            url: product.url,
            id: String(product.id),
          }));
        }

        throw new Error(response?.statusText || 'Failed to fetch recommendations');
      }

      /**
       * @param {import('./types').ClerkProductData[]} products - The Clerk products to fetch cards for
       * @returns {Promise<HTMLElement[]>} - The product cards
       */
      async getProductCards(products) {
        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        if (!products || !Array.isArray(products) || products.length === 0) {
          return [];
        }

        try {
          const requests = products.map(async (product) => {
            const response = await fetch(
              `${window.Shopify.routes.root}${product.url.slice(1)}?section_id=section-main-product`,
              {
                method: 'GET',
                signal,
              },
            );

            if (!response.ok) return null;

            const data = await response.text();

            const html = new DOMParser().parseFromString(data, 'text/html');

            const json = html.querySelector('[data-product-card-json]');

            if (!json || !json.textContent) return null;

            const cardHtml = Object.values(JSON.parse(json.textContent))[0];

            const item = document.createElement('li');
            item.classList.add('swiper-slide');
            item.dataset.clerkProductId = String(product.id);
            item.innerHTML = cardHtml;

            const available = item.querySelector('[data-available]')?.getAttribute('data-available');

            if (available !== 'true') return null;

            return item;
          });

          const items = await Promise.all(requests);

          return items.filter((item) => item !== null);
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            return [];
          }
          return [];
        }
      }
    },
  );
}
